import React from 'react';
import Select, {
	components,
	MenuListProps,
	MenuProps,
	ControlProps,
	DropdownIndicatorProps,
	IndicatorSeparatorProps,
	CSSObjectWithLabel,
	MultiValue,
	SingleValue,
} from 'react-select';
import { FieldError } from 'react-hook-form';
import classes from './styles.module.scss';

export interface ISelectOptions {
	value: string;
	label: string;
}
export function isMultiValue(
	item: MultiValue<ISelectOptions> | SingleValue<ISelectOptions>,
): item is MultiValue<ISelectOptions> {
	return false;
}

const customStyles = {
	menuList: (provided: CSSObjectWithLabel) => ({
		...provided,
		paddingBottom: 0,
		paddingTop: 0,
	}),
	clearIndicator: () => ({
		marginTop: '4px',
	}),
	control: (provided: CSSObjectWithLabel) => ({
		...provided,
		borderStyle: 'hidden',
		borderBottom: '1.5px solid rgba(97,66,117,.1)',
		borderRadius: 0,
		':hover': {
			borderColor: 'rgba(97,66,117,.1)',
		},
		minHeight: 'auto',
		boxShadow: 'none',
	}),
	option: (provided: CSSObjectWithLabel, { isFocused }: { isFocused: boolean }) => ({
		...provided,
		backgroundColor: isFocused ? 'rgba(0, 0, 0, 0.16)' : 'white',
		color: isFocused ? '#000 !important' : '#6d6e6f',
	}),
	menu: (provided: CSSObjectWithLabel) => ({
		...provided,
		marginTop: 0,
	}),
};

const Menu = (props: MenuProps<ISelectOptions>) => {
	return <components.Menu {...props}>{props.children}</components.Menu>;
};

const createMenuList = (menuHeight?: string) => {
	return (props: MenuListProps<ISelectOptions>) => {
		const maxHeight = menuHeight || '300px';
		return (
			<components.MenuList 
				{...props} 
				className={classes['select-items']}
				innerProps={{
					...props.innerProps,
					style: {
						...(props.innerProps?.style || {}),
						maxHeight,
					},
				}}
			>
				{props.children}
			</components.MenuList>
		);
	};
};

const Control = (props: ControlProps<ISelectOptions>) => {
	const { selectProps } = props;
	return (
		<components.Control
			{...props}
			className={`${classes['select-selected']} ${selectProps.menuIsOpen && classes['select-arrow-active']}`}
		>
			{props.children}
		</components.Control>
	);
};

const DropdownIndicator = (props: DropdownIndicatorProps<ISelectOptions>) => {
	return null;
};

const IndicatorSeparator = ({ innerProps }: IndicatorSeparatorProps<ISelectOptions>) => {
	return null;
};

// Константные компоненты для сообщений
const NoOptionsMessage = () => <>Не найдено</>;
const LoadingMessage = () => <>Загружается</>;

export default function CustomSelect({
	options,
	onChange,
	placeholder = '',
	error,
	isClearable = false,
	label,
	defaultValue,
	value,
	isSearchable = false,
	isDisabled = false,
	width,
	menuHeight,
}: {
	options: ISelectOptions[];
	onChange: (value: any) => void;
	placeholder?: string;
	error?: FieldError;
	isClearable?: boolean;
	label?: React.ReactNode;
	defaultValue?: number | string;
	isSearchable?: boolean;
	isDisabled?: boolean;
	value?: ISelectOptions;
	width?: string;
	menuHeight?: string;
}) {
	const handleChange = (newValue: MultiValue<ISelectOptions> | SingleValue<ISelectOptions>): void => {
		console.log({ newValue });
		if (isMultiValue(newValue) || !newValue) {
			onChange(newValue);
		} else {
			onChange(newValue.value);
		}
	};

	const currentValue =
		value !== undefined
			? options.find(opt => opt.value === value?.value)
			: options.find(opt => opt.value === defaultValue);

	const MenuList = React.useMemo(() => createMenuList(menuHeight), [menuHeight]);

	return (
		<label className={classes.label}>
			{label && <div className={classes['label__title']}>{label}</div>}
		<Select
			styles={{
				...customStyles,
				container: provided => ({
					...provided,
					width: width || provided.width,
				}),
				menu: provided => ({
					...customStyles.menu(provided),
					...(menuHeight && { maxHeight: menuHeight }),
				}),
				menuList: provided => ({
					...customStyles.menuList(provided),
					...(menuHeight && { maxHeight: menuHeight }),
				}),
			}}
				className={`${classes['styled-select']} ${classes['styled-select--md']}`}
				options={options}
				// menuIsOpen
				isSearchable={isSearchable}
				isClearable={isClearable}
				isDisabled={isDisabled}
				onChange={handleChange}
				placeholder={placeholder}
			noOptionsMessage={NoOptionsMessage}
			loadingMessage={LoadingMessage}
			components={{ MenuList, Menu, Control, DropdownIndicator, IndicatorSeparator }}
			value={currentValue}
			menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
		/>
			{error && <small className={classes['label__error']}>{error.message}</small>}
		</label>
	);
}
